import Icon from "@/components/Icon";
import IconButton from "@/components/IconButton";
import TextField from "@/components/TextField";
import classNames from "classnames";
import {
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
  type RefObject
} from "react";
import { createPortal } from "react-dom";
import type { SortingState } from "@tanstack/react-table";
import type { MaterialNames } from "@/components/material_symbol_names";
import type { RosterResultsFilters } from "../rosterEventFields";

export const ROSTER_HEADER_META = {
  headerClassName: "hide-header-in-column align-top !items-start"
} as const;

export type RosterResultsHeaderContextValue = {
  filters: RosterResultsFilters;
  openHeaderFilters: Partial<Record<keyof RosterResultsFilters, boolean>>;
  sorting: SortingState;
  onToggleSort: (columnId: string) => void;
  onFilterChange: (key: keyof RosterResultsFilters, value: string) => void;
  onFilterOpenChange: (key: keyof RosterResultsFilters, open: boolean) => void;
};

const RosterResultsHeaderContext = createContext<RosterResultsHeaderContextValue | null>(null);

export function RosterResultsHeaderProvider({
  value,
  children
}: {
  value: RosterResultsHeaderContextValue;
  children: ReactNode;
}) {
  return <RosterResultsHeaderContext.Provider value={value}>{children}</RosterResultsHeaderContext.Provider>;
}

function useRosterResultsHeader() {
  const context = useContext(RosterResultsHeaderContext);
  if (!context) {
    throw new Error("Roster results header components must render within RosterResultsHeaderProvider");
  }
  return context;
}

export function getColumnSortDirection(sorting: SortingState, columnId: string): false | "asc" | "desc" {
  const match = sorting.find((item) => item.id === columnId);
  if (!match) return false;
  return match.desc ? "desc" : "asc";
}

function sortIconName(direction: false | "asc" | "desc"): MaterialNames {
  if (direction === "asc") return "arrow_upward";
  if (direction === "desc") return "arrow_downward";
  return "swap_vert";
}

const HEADER_LABEL_ROW_HEIGHT = "h-6";
const HEADER_FILTER_INPUT_HEIGHT = "h-9";
const FILTER_POPOVER_MIN_WIDTH = 180;

type SortControlProps = {
  label: string;
  columnId: string;
  sorting: SortingState;
  onToggleSort: (columnId: string) => void;
};

function HeaderLabelText({ label }: { label: string }) {
  return (
    <span className="truncate whitespace-nowrap font-serif text-body-large-bold text-sys-brand-on-surface">
      {label}
    </span>
  );
}

function SortButton({ label, columnId, sorting, onToggleSort }: SortControlProps) {
  const direction = getColumnSortDirection(sorting, columnId);
  const isSorted = direction !== false;

  return (
    <button
      type="button"
      className={classNames(
        "flex w-full min-w-0 flex-1 items-center justify-between gap-2 rounded-sm text-left",
        HEADER_LABEL_ROW_HEIGHT,
        "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sys-brand-primary"
      )}
      aria-label={`Sort by ${label}${isSorted ? `, ${direction === "asc" ? "ascending" : "descending"}` : ""}`}
      onClick={(event) => {
        event.stopPropagation();
        onToggleSort(columnId);
      }}
      data-testid={`roster-sort-${columnId}`}
    >
      <HeaderLabelText label={label} />
      <span
        className={classNames(
          "flex shrink-0 items-center justify-center rounded-sm",
          HEADER_LABEL_ROW_HEIGHT,
          "w-6",
          isSorted && "bg-sys-brand-primary-container"
        )}
      >
        <Icon name={sortIconName(direction)} size={18} className="shrink-0 text-sys-brand-on-surface-variant" />
      </span>
    </button>
  );
}

const HeaderFilterToggle = forwardRef<
  HTMLButtonElement,
  {
    label: string;
    filterName: string;
    isActive: boolean;
    isOpen: boolean;
    popoverId: string;
    onToggle: () => void;
  }
>(function HeaderFilterToggle({ label, filterName, isActive, isOpen, popoverId, onToggle }, ref) {
  return (
    <button
      ref={ref}
      type="button"
      aria-label={`Filter ${label}`}
      aria-expanded={isOpen}
      aria-pressed={isOpen || isActive}
      aria-controls={popoverId}
      className={classNames(
        "flex shrink-0 items-center justify-center rounded-sm",
        HEADER_LABEL_ROW_HEIGHT,
        "w-6 hover:bg-sys-brand-surface-container-high",
        "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sys-brand-primary",
        (isOpen || isActive) && "bg-sys-brand-primary-container"
      )}
      onClick={(event) => {
        event.stopPropagation();
        onToggle();
      }}
      data-testid={`roster-filter-toggle-${filterName}`}
    >
      <Icon name="filter_list" size={18} className="text-sys-brand-on-surface-variant" />
    </button>
  );
});

type FilterPopoverPosition = {
  top: number;
  left: number;
  width: number;
};

function HeaderFilterPopover({
  anchorRef,
  toggleRef,
  label,
  name,
  popoverId,
  value,
  inputRef,
  onChange,
  onClose
}: {
  anchorRef: RefObject<HTMLElement | null>;
  toggleRef: RefObject<HTMLButtonElement | null>;
  label: string;
  name: string;
  popoverId: string;
  value: string;
  inputRef: RefObject<HTMLInputElement>;
  onChange: (value: string) => void;
  onClose: () => void;
}) {
  const popoverRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<FilterPopoverPosition | null>(null);

  const updatePosition = useCallback(() => {
    const anchor = anchorRef.current;
    if (!anchor) return;

    const columnCell = anchor.closest("th") ?? anchor;
    const rect = columnCell.getBoundingClientRect();
    setPosition({
      top: rect.top - 4,
      left: rect.left,
      width: Math.max(FILTER_POPOVER_MIN_WIDTH, rect.width)
    });
  }, [anchorRef]);

  useLayoutEffect(() => {
    updatePosition();
  }, [updatePosition]);

  useEffect(() => {
    const handleReposition = () => updatePosition();
    window.addEventListener("resize", handleReposition);
    window.addEventListener("scroll", handleReposition, true);
    return () => {
      window.removeEventListener("resize", handleReposition);
      window.removeEventListener("scroll", handleReposition, true);
    };
  }, [updatePosition]);

  useEffect(() => {
    if (!position) return;
    requestAnimationFrame(() => inputRef.current?.focus());
  }, [inputRef, position]);

  useEffect(() => {
    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (toggleRef.current?.contains(target) || popoverRef.current?.contains(target)) return;
      onClose();
    };

    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [onClose, toggleRef]);

  if (!position) return null;

  return createPortal(
    <div
      ref={popoverRef}
      id={popoverId}
      role="dialog"
      aria-label={`${label} filter`}
      className={classNames(
        "fixed z-50 -translate-y-full rounded-md border border-sys-brand-outline-variant",
        "bg-sys-brand-surface-container-lowest p-1 shadow-lg"
      )}
      style={{
        top: position.top,
        left: position.left,
        width: position.width
      }}
      onMouseDown={(event) => event.stopPropagation()}
      onClick={(event) => event.stopPropagation()}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node)) {
          onClose();
        }
      }}
      onKeyDown={(event) => {
        if (event.key === "Escape") {
          event.preventDefault();
          event.stopPropagation();
          onClose();
        }
      }}
      data-testid={popoverId}
    >
      <div className={classNames("relative w-full", HEADER_FILTER_INPUT_HEIGHT, value && "pr-10")}>
        <TextField
          ref={inputRef}
          label={label}
          name={name}
          value={value}
          leadingIcon="search"
          placeholder="Filter"
          className="w-full [&_md-outlined-text-field]:!min-h-9"
          onChangeText={onChange}
          onKeyDown={(event) => {
            event.stopPropagation();
            if (event.key === "Escape" || event.key === "Enter") {
              event.preventDefault();
              onClose();
            }
          }}
        />
        {value ? (
          <IconButton
            name="close"
            ariaLabel={`Clear ${label} filter`}
            variant="square"
            skipMargin
            className="!absolute right-0 top-1/2 z-10 -translate-y-1/2"
            data-testid={`${name}-clear`}
            onClick={(event) => {
              event.stopPropagation();
              onChange("");
              onClose();
            }}
          />
        ) : null}
      </div>
    </div>,
    document.body
  );
}

const ColumnHeaderShell = forwardRef<HTMLDivElement, { children: ReactNode }>(function ColumnHeaderShell(
  { children },
  ref
) {
  return (
    <div ref={ref} className="flex w-full min-w-[5.5rem] flex-col">
      {children}
    </div>
  );
});

export function SortColumnHeader({ label, columnId, sorting, onToggleSort }: SortControlProps) {
  return (
    <ColumnHeaderShell>
      <SortButton label={label} columnId={columnId} sorting={sorting} onToggleSort={onToggleSort} />
    </ColumnHeaderShell>
  );
}

type FilterSortColumnHeaderProps = {
  label: string;
  columnId: string;
  filterKey: keyof RosterResultsFilters;
  filterName: string;
};

function FilterSortColumnHeader({ label, columnId, filterKey, filterName }: FilterSortColumnHeaderProps) {
  const { filters, openHeaderFilters, sorting, onToggleSort, onFilterChange, onFilterOpenChange } =
    useRosterResultsHeader();
  const columnRef = useRef<HTMLDivElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const filterValue = filters[filterKey];
  const filterOpen = openHeaderFilters[filterKey] ?? false;
  const hasActiveFilter = Boolean(filterValue.trim());
  const popoverId = `roster-filter-popover-${filterName}`;

  const closeFilter = useCallback(() => onFilterOpenChange(filterKey, false), [filterKey, onFilterOpenChange]);
  const toggleFilter = () => onFilterOpenChange(filterKey, !filterOpen);

  return (
    <ColumnHeaderShell ref={columnRef}>
      <div className={classNames("flex min-w-0 items-center gap-1", HEADER_LABEL_ROW_HEIGHT)}>
        <SortButton label={label} columnId={columnId} sorting={sorting} onToggleSort={onToggleSort} />
        <HeaderFilterToggle
          ref={toggleRef}
          label={label}
          filterName={filterName}
          isActive={hasActiveFilter}
          isOpen={filterOpen}
          popoverId={popoverId}
          onToggle={toggleFilter}
        />
      </div>
      {filterOpen ? (
        <HeaderFilterPopover
          anchorRef={columnRef}
          toggleRef={toggleRef}
          label={label}
          name={filterName}
          popoverId={popoverId}
          value={filterValue}
          inputRef={inputRef}
          onChange={(value) => onFilterChange(filterKey, value)}
          onClose={closeFilter}
        />
      ) : null}
    </ColumnHeaderShell>
  );
}

export function PlainColumnHeader({ label }: { label: string }) {
  return (
    <ColumnHeaderShell>
      <div className={classNames("flex min-w-0 items-center", HEADER_LABEL_ROW_HEIGHT)}>
        <HeaderLabelText label={label} />
      </div>
    </ColumnHeaderShell>
  );
}

export function FirstNameColumnHeader() {
  return (
    <FilterSortColumnHeader
      label="First Name"
      columnId="firstName"
      filterKey="firstName"
      filterName="roster-filter-first-name"
    />
  );
}

export function LastNameColumnHeader() {
  return (
    <FilterSortColumnHeader
      label="Last Name"
      columnId="lastName"
      filterKey="lastName"
      filterName="roster-filter-last-name"
    />
  );
}

export function FacilityColumnHeader() {
  return (
    <FilterSortColumnHeader
      label="Facility"
      columnId="facilityName"
      filterKey="facility"
      filterName="roster-filter-facility"
    />
  );
}
